import React, { memo, useState } from 'react';
import { Table, Thead, Tbody, Tr, Th, Td } from '@patternfly/react-table';
import { Label, Tooltip, Content, ContentVariants, ButtonVariant, Button, Level, LevelItem } from '@patternfly/react-core';
import { FiCircle, FiCheckCircle } from "react-icons/fi";
import CopyText from "shared/utils/CopyText";

// Pobieranie PDF/Plików
const GetMediaUrl = (url, file_name) => {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('target', '_blank');
  link.setAttribute('rel', 'noreferrer');
  link.setAttribute('download', file_name);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Optymistyczny update rozwiązania
const ShareExerciseSolution = async (props, updateExercise, exercise_id, value) => {
  updateExercise(exercise_id, Boolean(value));
  try {
    const requestOptions = {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ share: value })
    };
    const link = `${props.state.api_url}/ti/api/v1/${props.state.organization_symbol}/${props.state.branch_symbol}/share_exercise_solution/${exercise_id}`;
    const resp = await fetch(link, requestOptions);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  } catch (err) {
    console.error('ShareExerciseSolution error:', err);
    updateExercise(exercise_id, !Boolean(value));
  }
};

// Memoizacja pojedynczego wiersza w tabeli
const ExerciseRow = memo(({
    ex,
    header,
    state,
    context,
    props,
    updateExercise,
    isSelected,
    onSelect
  }) => {
  const [index, setIndex] = useState(0);

  const handleClick = () => {
    setIndex(prev => (prev + 1) % ex.exercise_number.length);
  };

  // 🔹 dostęp na podstawie visibility + superuser + public niezalogowani
  const canAccess = context.state.userProfile.is_superuser ||
    (context.state.isAuthUser && (ex.visibility === 'authenticated' || ex.visibility === 'public')) ||
    (!context.state.isAuthUser && ex.visibility === 'public');

  return (
    <Tr
      isSelectable
      // isClickable
      isRowSelected={isSelected}
      onRowClick={() => onSelect(ex)}
    >
      {/* Numer zadania */}
      <Td dataLabel={header[0].label}>
        {isSelected  
          ? (
            <>
              <Label variant="outline" status="success">
                {ex.exercise_id}
              </Label>
            </>
          )
          : <span style={{ color: 'gray' }}>{ex.exercise_id}</span>
        }
      </Td>
      {/* Zadania */}
      <Td dataLabel={header[1].label}>
        <Level>
          <LevelItem>            
            {ex.exercise_pdf_url ? (
              canAccess ? (
                ex.mockup_url ? (
                  <Tooltip
                    position="right"
                    content={
                      <div style={{ maxWidth: '150px', textAlign: 'center' }}>
                        <img
                          src={`${state.api_url}${ex.mockup_url}`}
                          alt={`Podgląd ${ex.exercise_name}`}
                          style={{ maxWidth: '100%', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}
                        />
                      </div>
                    }
                  >
                    <Content
                      component={ContentVariants.a}
                      target="_blank"
                      onClick={() => GetMediaUrl(`${state.api_url}${ex.exercise_pdf_url}`, ex.exercise_name)}
                      style={{ cursor: 'pointer', textDecoration: 'none', display: 'inline-block' }}
                    >
                      {ex.exercise_name}
                    </Content>
                  </Tooltip>
                ) : (
                  <Content
                    component={ContentVariants.a}
                    target="_blank"
                    onClick={() => GetMediaUrl(`${state.api_url}${ex.exercise_pdf_url}`, ex.exercise_name)}
                    style={{ cursor: 'pointer', textDecoration: 'none', display: 'inline-block' }}
                  >
                    {ex.exercise_name}
                  </Content>
                )
              ) : (
                <span style={{ color: 'gray' }}>{ex.exercise_name}</span>
              )
            ) : (
              <span>{ex.exercise_name}</span>
            )}
          </LevelItem>
          <LevelItem>
            <CopyText
              text={ex.exercise_name}
              id={ex.exercise_number}
              style={{ marginLeft: "10px" }}
            />
          </LevelItem>
        </Level>
      </Td>
      {/* Zagadnienie */}
      {canAccess ? (
        <Td dataLabel={header[2].label}>{ex.chapter_name}</Td>
      ) : (
        <Td dataLabel={header[2].label}><span style={{ color: 'gray' }}>{ex.chapter_name}</span></Td>
      )}
      {/* Tytuł */}
      {canAccess ? (
        <Td dataLabel={header[3].label}>{ex.exercise_title}</Td>
      ) : (
        <Td dataLabel={header[3].label}><span style={{ color: 'gray' }}>{ex.exercise_title}</span></Td>
      )}
      {/* Załączniki */}
      <Td dataLabel={header[4].label}>
        {ex.attachments && ex.attachments.length > 0 && (
          ex.attachments.map((att, idx) => {
            let label = att.type === 'zip' ? '[zip]' : att.type === '7z' ? '[7z]' : `[${att.type}]`;
            if (!canAccess) {
              return (
                <Tooltip key={idx} content={`Załącznik do ${ex.exercise_name}`}>
                  <span style={{ marginRight: '8px', color: 'gray' }}>{label}</span>
                </Tooltip>
              );
            }
            return (
              <Tooltip key={idx} content={`Załącznik do ${ex.exercise_name}`}>
                <Content
                  key={idx}
                  component={ContentVariants.a}
                  target="_blank"
                  onClick={() => GetMediaUrl(`${state.api_url}${att.url}`, `${ex.exercise_name}-attachment-${idx}`)}
                  style={{ marginRight: '8px', cursor: 'pointer' }}
                >
                  {label}
                </Content>
              </Tooltip>
            );
          })
        )}
      </Td>
      {/* Grupy */}
      <Td dataLabel={header[5].label}>
        {Object.entries(ex.groups_completed).map(([g, info]) => (
          <Tooltip key={g} content={<div>Zrealizowano: [ {info.last_update} ]</div>}>
            <Label isCompact style={{ marginRight: '5px' }} color="gray">{info.group_symbol}</Label>
          </Tooltip>
        ))}
      </Td>
      {/* Rozwiązania */}
      <Td dataLabel={header[6].label}>
        {ex.solutions && ex.solutions.length > 0 && (
          ex.solutions.map((sol, idx) => {
            let label = sol.type === 'pdf' ? '[pdf]' : sol.type === 'zip' ? '[zip]' : sol.type === '7z' ? '[7z]' : '[url]';

            if (!ex.solution_shared || !canAccess) {
              return (
                <Tooltip key={idx} content={`Rozwiązanie do ${ex.exercise_name}`}>
                  <span style={{ marginRight: '8px', color: 'gray' }}>{label}</span>
                </Tooltip>
              );
            }

            if (sol.type === 'external_url') {
              return (
                <Tooltip key={idx} content={`Repozytorium do ${ex.exercise_name}`}>
                  <Content
                    key={idx}
                    component={ContentVariants.a}
                    target="_blank"
                    href={sol.url}
                    style={{ marginRight: '8px', cursor: 'pointer' }}
                  >
                    {label}
                  </Content>
                </Tooltip>
              );
            }
            return (
              <Tooltip key={idx} content={`Rozwiązanie do ${ex.exercise_name}`}>
                <Content
                  key={idx}
                  component={ContentVariants.a}
                  target="_blank"
                  onClick={() =>
                    GetMediaUrl(`${state.api_url}${sol.url}`, `${ex.exercise_name}-solution-${idx}`)
                  }
                  style={{ marginRight: '8px', cursor: 'pointer' }}
                >
                  {label}
                </Content>
              </Tooltip>
            );
          })
        )}
      </Td>
      {/* Udostęnienie rozwiązania */}
      <Td>
        {context.state.userProfile.is_superuser && ex.solutions.length > 0 && (
          ex.solution_shared
            ? <Tooltip content={<div>Udostępnienie wygaśnie o {ex.solution_public_until}</div>}>
              <Button variant={ButtonVariant.plain} aria-label="Copy" size="sm">
                <FiCheckCircle onClick={() => ShareExerciseSolution(props, updateExercise, ex.exercise_id, 0)} />
              </Button>
            </Tooltip>
            : <Button variant={ButtonVariant.plain} aria-label="Copy" size="sm">
              <FiCircle onClick={() => ShareExerciseSolution(props, updateExercise, ex.exercise_id, 1)} />
            </Button>
        )}
      </Td>
    </Tr>
  );
});

export const ExercisesTable = ({
  props,
  header,
  exercises,
  state,
  context,
  updateExercise
}) => {

  const [selectedExerciseId, setSelectedExerciseId] = useState(null);

  if (!exercises) return <p>Brak danych do wyświetlenia.</p>;

  return (
    <Table
      variant="compact"
      aria-label="Tabela ćwiczeń"
    >
      <Thead>
        <Tr>
          {header.map((h, idx) => (
            <Th key={idx}>{h.label}</Th>
          ))}
        </Tr>
      </Thead>

      <Tbody>
        {exercises.map(ex => (
          <ExerciseRow
            key={ex.exercise_id}
            ex={ex}
            header={header}
            state={state}
            context={context}
            props={props}
            updateExercise={updateExercise}
            isSelected={selectedExerciseId === ex.exercise_id}
            onSelect={(exercise) =>
              setSelectedExerciseId(exercise.exercise_id)
            }
          />
        ))}
      </Tbody>
    </Table>
  );
};